import React, { useState, useEffect, useRef } from 'react';
import UwinDataPoints from '../DataPoints/Uwin/UwinDataPoints';
import JananiDataPoints from '../DataPoints/Janani/JananiDataPoints';
import RbskDataPoints from '../DataPoints/Rbsk/RbskDataPoints';
import OthersDataPoints from '../DataPoints/Others/OthersDataPoints';
import './AdminDashboard.css';

const AdminDashboard = () => {
    const [activeTab, setActiveTab] = useState('UWIN');
    const [activeTotals, setActiveTotals] = useState(null);
    const [sliderStyle, setSliderStyle] = useState({ left: 0, top: 0, width: 0, height: 0, opacity: 0 });
    const tabsContainerRef = useRef(null);

    // Reset totals when switching tabs
    useEffect(() => {
        setActiveTotals(null);
    }, [activeTab]);

    // Handle tab sliding effect
    useEffect(() => {
        const updateSlider = () => {
            if (tabsContainerRef.current) {
                const activeElement = tabsContainerRef.current.querySelector('.custom-tab.active');
                if (activeElement) {
                    setSliderStyle({
                        left: activeElement.offsetLeft,
                        top: activeElement.offsetTop,
                        width: activeElement.offsetWidth,
                        height: activeElement.offsetHeight,
                        opacity: 1
                    });
                }
            }
        };

        updateSlider();
        
        // Ensure accurate measurement after browser paints
        let frameId = requestAnimationFrame(() => {
            frameId = requestAnimationFrame(updateSlider);
        });

        window.addEventListener('resize', updateSlider);
        return () => {
            cancelAnimationFrame(frameId);
            window.removeEventListener('resize', updateSlider);
        };
    }, [activeTab]);

    return (
        <section className="container mt-4">
            <div className="d-flex mb-4 flex-wrap">
                <div>
                    <div className="custom-tabs-container mb-3" ref={tabsContainerRef}>
                        <div className="tab-slider" style={sliderStyle} />
                        <button
                            className={`custom-tab ${activeTab === 'UWIN' ? 'active' : ''}`}
                            onClick={() => setActiveTab('UWIN')}
                        >
                            {/* <i className={`bi bi-globe tab-icon ${activeTab === 'UWIN' ? 'globe-icon' : ''}`}></i> */}
                            <img
                                src="/Images/uwin-icon.svg"
                                alt="U-WIN Logo"
                                className="me-2"
                                style={{ width: '20px', height: '20px' }}
                            />
                            U-WIN
                        </button>
                        <button
                            className={`custom-tab ${activeTab === 'JANANI' ? 'active' : ''}`}
                            onClick={() => setActiveTab('JANANI')}
                        >
                            <img
                                src="/Images/janani-icon.svg"
                                alt="Janani Logo"
                                className="me-2"
                                style={{ width: '20px', height: '20px' }}
                            />
                            JANANI
                        </button>

                        <button
                            className={`custom-tab ${activeTab === 'RBSK' ? 'active' : ''}`}
                            onClick={() => setActiveTab('RBSK')}
                        >
                            <img
                                src="/Images/rbsk-icon.svg"
                                alt="RBSK Logo"
                                className="me-2"
                                style={{ width: '20px', height: '20px' }}
                            />
                            RBSK
                        </button>
                        <button
                            className={`custom-tab ${activeTab === 'OTHERS' ? 'active' : ''}`}
                            onClick={() => setActiveTab('OTHERS')}
                        >
                            <img
                                src="/Images/others-icon.svg"
                                alt="Others Logo"
                                className="me-2"
                                style={{ width: '20px', height: '20px' }}
                            />
                            OTHERS
                        </button>
                    </div>
                </div>

                {/* Shared Top Metrics Cards and Progress Bar */}
                <div className="ms-auto d-flex flex-column gap-2" style={{ maxWidth: '500px', width: '100%', minHeight: '84px' }}>
                    <div className="d-flex gap-2 justify-content-end mb-3">
                        <div className="card px-2 py-1 border-0 shadow-sm text-center d-flex flex-column justify-content-center" style={{ backgroundColor: '#f1f8f3', flex: 1, borderRadius: '6px' }}>
                            <small className="fw-bold text-nowrap" style={{ fontSize: '9px', color: '#495057' }}>NON ANAEMIC</small>
                            <span className="fw-bold" style={{ color: '#4CAF50', fontSize: '15px' }}>
                                {activeTotals ? (activeTotals.total_normal || 0).toLocaleString() : '-'}
                            </span>
                        </div>
                        <div className="card px-2 py-1 border-0 shadow-sm text-center d-flex flex-column justify-content-center" style={{ backgroundColor: '#fffcf2', flex: 1, borderRadius: '6px' }}>
                            <small className="fw-bold text-nowrap" style={{ fontSize: '9px', color: '#495057' }}>MILD</small>
                            <span className="fw-bold" style={{ color: '#FFC107', fontSize: '15px' }}>
                                {activeTotals ? (activeTotals.total_mild || 0).toLocaleString() : '-'}
                            </span>
                        </div>
                        <div className="card px-2 py-1 border-0 shadow-sm text-center d-flex flex-column justify-content-center" style={{ backgroundColor: '#fff6ec', flex: 1, borderRadius: '6px' }}>
                            <small className="fw-bold text-nowrap" style={{ fontSize: '9px', color: '#495057' }}>MODERATE</small>
                            <span className="fw-bold" style={{ color: '#FF9800', fontSize: '15px' }}>
                                {activeTotals ? (activeTotals.total_moderate || 0).toLocaleString() : '-'}
                            </span>
                        </div>
                        <div className="card px-2 py-1 border-0 shadow-sm text-center d-flex flex-column justify-content-center" style={{ backgroundColor: '#fdf1f1', flex: 1, borderRadius: '6px' }}>
                            <small className="fw-bold text-nowrap" style={{ fontSize: '9px', color: '#495057' }}>SEVERE</small>
                            <span className="fw-bold" style={{ color: '#F44336', fontSize: '15px' }}>
                                {activeTotals ? (activeTotals.total_severe || 0).toLocaleString() : '-'}
                            </span>
                        </div>
                    </div>

                    <div className="progress w-100" style={{ height: '14px', borderRadius: '4px', backgroundColor: '#e9ecef', overflow: 'hidden' }}>
                        {(() => {
                            let pNon = 0, pMild = 0, pMod = 0, pSev = 0;
                            if (activeTotals) {
                                const totalAll = (activeTotals.total_normal || 0) + (activeTotals.total_mild || 0) + (activeTotals.total_moderate || 0) + (activeTotals.total_severe || 0);
                                if (totalAll > 0) {
                                    pNon = ((activeTotals.total_normal || 0) / totalAll) * 100;
                                    pMild = ((activeTotals.total_mild || 0) / totalAll) * 100;
                                    pMod = ((activeTotals.total_moderate || 0) / totalAll) * 100;
                                    pSev = ((activeTotals.total_severe || 0) / totalAll) * 100;
                                }
                            }

                            return (
                                <>
                                    <div className="progress-bar" style={{ width: `${pNon}%`, backgroundColor: '#4CAF50', transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)' }} title={`Non Anaemic: ${pNon.toFixed(1)}%`}></div>
                                    <div className="progress-bar" style={{ width: `${pMild}%`, backgroundColor: '#FFC107', transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)' }} title={`Mild: ${pMild.toFixed(1)}%`}></div>
                                    <div className="progress-bar" style={{ width: `${pMod}%`, backgroundColor: '#FF9800', transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)' }} title={`Moderate: ${pMod.toFixed(1)}%`}></div>
                                    <div className="progress-bar" style={{ width: `${pSev}%`, backgroundColor: '#F44336', transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)' }} title={`Severe: ${pSev.toFixed(1)}%`}></div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            </div>

            <div className="tab-content">
                {activeTab === 'UWIN' && <UwinDataPoints onTotalsCalculated={setActiveTotals} />}
                {activeTab === 'JANANI' && <JananiDataPoints onTotalsCalculated={setActiveTotals} />}
                {activeTab === 'RBSK' && <RbskDataPoints onTotalsCalculated={setActiveTotals} />}
                {activeTab === 'OTHERS' && <OthersDataPoints onTotalsCalculated={setActiveTotals} />}
            </div>
        </section>
    );
};

export default AdminDashboard;